import { Injectable } from '@angular/core';
import { BgBomItem, BgBomSubCategory, BomType } from 'src/app/shared/models/bg.model';
import { BackendApiService } from './backend-api.service';

@Injectable({
  providedIn: 'root'
})
export class BomCalculatorService {
  // Parameters for lumber calculations
  public readonly eachLumberlengthParam: string = 'parentLumberEachlength';
  public readonly lumberLengthParam: string = 'lumberLengthParam';
  public readonly sizeLumberParam: string = 'sizeLumberParam';

  constructor(private bgModelService: BackendApiService) {}

  /**
   * Gets a parameter value as a number from a BgBomItem
   */
  public getParameterAsNumber(item: BgBomItem, paramName: string): number {
    const param = item.parameters?.find(p => p.name === paramName);
    return Number(param?.valueDouble ?? 0);
  }

   /**
   * Gets a parameter value as a string from a BgBomItem
   */
  public getParameterAsString(item: BgBomItem, paramName: string): string {
    const param = item.parameters?.find(p => p.name === paramName);
    return String(param?.valueString ?? '');
  }

  /**
   * Sets or updates a parameter on a BgBomItem
   */
  public setOrUpdateParameter(item: BgBomItem, paramName: string, value: string, measurement?: string) {
    let param = item.parameters?.find(p => p.name === paramName);
    let numValue = isNaN(Number(value)) ? 0 : Number(value)
    if (!param) {

      param = { name: paramName, valueDouble: numValue.toString(), valueString: value, measurement: measurement };
      item.parameters = item.parameters || [];
      item.parameters.push(param);
    } else {
      param.valueDouble = numValue.toString();
      param.valueString = value;
      if (measurement) param.measurement = measurement;
    }
  }


  /**
   * Sets or updates a parameter on a BgBomItem with a number value
   */
  public setOrUpdateParameterNumber(item: BgBomItem, paramName: string, value: number, measurement?: string) {
    this.setOrUpdateParameter(item, paramName, value.toString(), measurement);
  }


  /**
   * Finds the parent lumber item for a child lumber item
   */
  public findParentLumberItem(childItem: BgBomItem, categoryItems: BgBomItem[]): BgBomItem | undefined {
    if (String(childItem.itemBomType) === BomType[BomType.LumberTotal]) {
      return childItem;
    }

    // Find the parent using parentId
    if (!childItem || !childItem['parentId']) return undefined;
    const parentId = (childItem as any).parentId;

    return categoryItems.find(i => i.id === parentId);
  }

  /**
   * Finds the residue item related to a lumber item
   */
  public findChildTotalLumberResidue(item: BgBomItem, categoryItems: BgBomItem[]): BgBomItem | undefined {
    if (String(item.itemBomType) === BomType[BomType.ChildTotalLumberResidue]) {
      return item;
    }

    // Get parent first
    const parent = this.findParentLumberItem(item, categoryItems) ?? item;

    // Find child items with matching parentId and type ChildTotalLumberResidue
    return categoryItems.find(i =>
      i.parentId === parent?.id &&
      (i.itemBomType === BomType.ChildTotalLumberResidue ||
       String(i.itemBomType) === BomType[BomType.ChildTotalLumberResidue])
    );
  }

  /**
   * Calculates the total length for a lumber item and its children
   */
  public calculateTotalLength(item: BgBomItem, categoryItems: BgBomItem[]): number {
    let id: string | undefined;
    let wasteFactor = 1.1;

    if (String(item?.itemBomType) === BomType[BomType.LumberTotal]) {
      id = item.id;
    } else {
      id = item.parentId ?? item.id;
    }

    const parent = categoryItems.find(i => i.id === id);
    const childs = categoryItems.filter(i => i.parentId === id);

    // Sum of parent length and children lengths
    let sum = 0;
    if (parent) {
      let parentLength = this.getParameterAsNumber(parent, this.lumberLengthParam);
      if (parentLength === 0) {
        parentLength = this.getParameterAsNumber(parent, 'length') * wasteFactor;
        this.setOrUpdateParameter(parent, this.lumberLengthParam, parentLength.toString(), 'ft');
      }
      sum += parentLength;
    }

    if (childs) {
      sum += childs.reduce((acc, i) => acc + this.getParameterAsNumber(i, this.lumberLengthParam), 0);
    }

    return sum;
  }

  /**
   * Checks if an item is a lumber item (any type)
   */
  public isTotalLumber(item: BgBomItem): boolean {
    let stringType = String(item.itemBomType);
    let isStringType = stringType === BomType[BomType.LumberTotal] ||
                      stringType === BomType[BomType.ChildTotalLumber] ||
                      stringType === BomType[BomType.ChildTotalLumberResidue];
    let isType = item.itemBomType === BomType.LumberTotal ||
                item.itemBomType === BomType.ChildTotalLumber ||
                item.itemBomType === BomType.ChildTotalLumberResidue;

    return isStringType || isType;
  }

  /**
   * Checks if an item is a parent lumber with children
   */
  public isTotalLumberWithChildren(item: BgBomItem, categoryItems: BgBomItem[]): boolean {
    let stringType = String(item.itemBomType);
    let isStringType = stringType === BomType[BomType.LumberTotal];
    let isType = item.itemBomType === BomType.LumberTotal;

    if (isStringType || isType) {
      let children = categoryItems.filter(i => i.parentId === item.id);
      return (children?.length ?? 0) > 0;
    }

    return false;
  }

  /**
   * Checks if an item is a child lumber item
   */
  public isChildTotalLumber(item: BgBomItem): boolean {
    let stringType = String(item.itemBomType);
    let isStringType = stringType === BomType[BomType.ChildTotalLumber];
    let isType = item.itemBomType === BomType.ChildTotalLumber;
    return isStringType || isType;
  }

  /**
   * Check if the lumber selection already contains the selected length
   */
  public containsLumberWithSameSection(item: BgBomItem, lumberLength: number, categoryItems: BgBomItem[]): boolean {
    // Find parent item
    let parentItem = this.findParentLumberItem(item, categoryItems) ?? item;

    // Find all items in the group
    let allGroupElements = categoryItems.filter(i => i.parentId === parentItem.id);
    allGroupElements.push(parentItem);

    for (let child of allGroupElements) {
      let size = this.getParameterAsNumber(child, this.sizeLumberParam);
      if (size === lumberLength && child.size !== item.size) {
        return true;
      }
    }

    return false;
  }

  /**
   * Calculates lumber values and updates the item and residue
   */
  public calculateLumber(
    selectedItem: BgBomItem,
    lumberLength: number,
    lumberQuantity: number,
    categoryItems: BgBomItem[]
  ): {
    updatedItem: BgBomItem,
    residueItem?: BgBomItem,
    newResidueItem?: BgBomItem,
    itemsToDelete?: BgBomItem[]
  } {
    const result = {
      updatedItem: selectedItem,
      residueItem: undefined as BgBomItem | undefined,
      newResidueItem: undefined as BgBomItem | undefined,
      itemsToDelete: [] as BgBomItem[]
    };

    if (!selectedItem || !lumberLength || !lumberQuantity) {
      return result;
    }

    let parentItem: BgBomItem = this.findParentLumberItem(selectedItem, categoryItems) ?? selectedItem;
    let totalLength = this.calculateTotalLength(parentItem, categoryItems);
    let parentLength = this.getParameterAsNumber(parentItem, 'length') * 1.1;
    let currentItemLength = this.getParameterAsNumber(selectedItem, this.lumberLengthParam);
    let residueBomItem = this.findChildTotalLumberResidue(selectedItem, categoryItems);
    let residueBomItemLength = 0;

    if (residueBomItem) {
      residueBomItemLength = this.getParameterAsNumber(residueBomItem, this.lumberLengthParam);
      if (selectedItem.itemBomType === BomType.ChildTotalLumberResidue ||
          String(selectedItem.itemBomType) === BomType[BomType.ChildTotalLumberResidue]) {
        residueBomItemLength = 0;
      }
    }

    const totalLengthOfUser = lumberLength * lumberQuantity;
    let difference = totalLengthOfUser - residueBomItemLength - currentItemLength;
    let size = String(selectedItem.size).split(' x ')[0];

    // Update the selected item with the new values
    selectedItem.modelQuantity = String(Math.ceil(lumberQuantity * 0.9));
    selectedItem.quantityToOrder = lumberQuantity.toString();
    selectedItem.size = size + " x " + String(lumberLength) + "'-0\"";
    selectedItem.modelQuantityDescription = 'each';
    selectedItem.quantityToOrderDescription = 'each' + " - " + String(lumberLength) + ' ' + 'ft';

    if (String(selectedItem.itemBomType) === BomType[BomType.ChildTotalLumberResidue] ||
        selectedItem.itemBomType === BomType.ChildTotalLumberResidue) {
      selectedItem.itemBomType = BomType.ChildTotalLumber;
    }

    this.setOrUpdateParameter(selectedItem, this.lumberLengthParam, totalLengthOfUser.toString(), 'ft');
    this.setOrUpdateParameter(selectedItem, this.sizeLumberParam, lumberLength.toString(), 'ft');

    if (difference <= 20 && difference >= -20) {
      // If difference is small, no need for residue item
      if (residueBomItem) {
        result.itemsToDelete = [residueBomItem];
      }
    } else {
      // Need to handle residue
      if (residueBomItem) {
        // Update existing residue item
        let fullLumberLength = this.calculateTotalLength(parentItem, categoryItems);
        let difference = fullLumberLength - parentLength - residueBomItemLength;
        let quantity = Math.ceil(difference * -1);

        residueBomItem.modelQuantity = String(Math.ceil((quantity * 0.9)));
        residueBomItem.quantityToOrder = quantity.toString();
        residueBomItem.modelQuantityDescription = 'lnft';
        residueBomItem.quantityToOrderDescription = 'lnft';

        this.setOrUpdateParameter(residueBomItem, this.lumberLengthParam, (difference * -1).toString(), 'ft');
        this.setOrUpdateParameter(selectedItem, this.sizeLumberParam, lumberLength.toString(), 'ft');

        result.residueItem = residueBomItem;
      } else {
        // Create a new residue item
        const allGroupItems = categoryItems.filter(i => i.parentId === parentItem.id || i.id === parentItem.id);
        const fullLumberLength = this.calculateTotalLength(parentItem, categoryItems);
        let quantity = Math.ceil((fullLumberLength - parentLength) * -1);

        const newChild: BgBomItem = {
          id: undefined,
          parentId: parentItem.id ?? '',
          flatIndex: undefined,
          group: selectedItem.group,
          order: (selectedItem.order ?? 0),
          itemName: selectedItem.itemName,
          itemBomType: BomType.ChildTotalLumberResidue,
          size: size,
          manufacturer: selectedItem.manufacturer,
          manufacturerModel: selectedItem.manufacturerModel,
          modelQuantity: String(Math.ceil((quantity * 0.9))),
          modelQuantityDescription: "lnft",
          quantity: lumberQuantity,
          wasteFactor: selectedItem.wasteFactor,
          quantityToOrder: String(quantity),
          quantityToOrderDescription: "lnft",
          parameters: [],
          material: '',
          description: ''
        };

        this.setOrUpdateParameter(newChild, this.lumberLengthParam, ((fullLumberLength - parentLength) * -1).toString(), 'ft');
        result.newResidueItem = newChild;
      }
    }

    return result;
  }

  /**
   * Delete a lumber item and update residue
   */
  public deleteLumberItem(
    item: BgBomItem,
    categoryItems: BgBomItem[]
  ): {
    residueItem?: BgBomItem,
    newResidueItem?: BgBomItem,
    itemToDelete: BgBomItem
  } {
    const result = {
      residueItem: undefined as BgBomItem | undefined,
      newResidueItem: undefined as BgBomItem | undefined,
      itemToDelete: item
    };

    let length = this.getParameterAsNumber(item, this.lumberLengthParam);
    let parentItem: BgBomItem = this.findParentLumberItem(item, categoryItems) ?? item;
    let residueBomItem = this.findChildTotalLumberResidue(item, categoryItems);
    let size = String(item.size).split(' x ')[0];

    if (residueBomItem) {
      // Update existing residue item
      let quantity = this.getParameterAsNumber(residueBomItem, this.lumberLengthParam);
      quantity = Math.ceil(quantity + length);
      residueBomItem.modelQuantity = String(Math.ceil(quantity * 0.9));
      residueBomItem.quantityToOrder = quantity.toString();
      residueBomItem.modelQuantityDescription = 'lnft';
      residueBomItem.quantityToOrderDescription = 'lnft';

      this.setOrUpdateParameter(residueBomItem, this.lumberLengthParam, quantity.toString(), 'ft');
      result.residueItem = residueBomItem;
    } else {
      // Create new residue item
      let quantity = Math.ceil(length);

      const newChild: BgBomItem = {
        id: undefined,
        parentId: parentItem.id ?? '',
        flatIndex: undefined,
        group: item.group,
        order: (item.order ?? 0),
        itemName: item.itemName,
        itemBomType: BomType.ChildTotalLumberResidue,
        size: size,
        manufacturer: item.manufacturer,
        manufacturerModel: item.manufacturerModel,
        modelQuantity: String(Math.ceil(length * 0.9)),
        modelQuantityDescription: "lnft",
        quantity: Math.ceil(length * 0.9),
        wasteFactor: item.wasteFactor,
        quantityToOrder: String(quantity),
        quantityToOrderDescription: "lnft",
        parameters: [],
        material: '',
        description: ''
      };

      this.setOrUpdateParameter(newChild, this.lumberLengthParam, length.toString(), 'ft');
      result.newResidueItem = newChild;
    }

    return result;
  }

  /**
   * Reset lumber settings to defaults
   */
  public resetTotalSettings(
    parent: BgBomItem
  ): BgBomItem {
    let size = String(parent.size).split(' x ')[0];
    let parentLength = this.getParameterAsNumber(parent, 'length');
    let quantity = Math.ceil(parentLength / 8);
    let quantityToOrder = Math.ceil(parentLength / 8 * 1.1);

    parent.modelQuantity = String(quantity);
    parent.quantityToOrder = String(quantityToOrder) + ' each - 8 ';
    parent.modelQuantityDescription = 'each';
    parent.quantityToOrderDescription = 'ft';
    parent.size = size + " x 8'-0\"";

    this.setOrUpdateParameter(parent, this.lumberLengthParam, (parentLength * 1.1).toString(), 'ft');
    this.setOrUpdateParameter(parent, this.sizeLumberParam, '8', 'ft');

    return parent;
  }

  // GYPSUM BOARD METHODS

  /**
   * Checks if an item is a gypsum board item
   */
  public isTotalGypsumBoard(item: BgBomItem): boolean {
    let stringType = String(item.itemBomType);
    let isStringType = stringType === BomType[BomType.GypsumBoardTotal];
    let isType = item.itemBomType === BomType.GypsumBoardTotal;
    return isStringType || isType;
  }

  public isGypsumBoard(item: BgBomItem): boolean {
    let stringType = String(item.itemBomType);
    let isStringType = stringType === BomType[BomType.GypsumBoard];
    let isType = item.itemBomType === BomType.GypsumBoard;
    return isStringType || isType;
  }

  /**
   * Applies gypsum board changes to an item
   */
  public applyGypsumChanges(
    item: BgBomItem,
    gypsumSize: string,
    gypsumSheets: number,
    totalGypsumArea: number
  ): BgBomItem {
    // Update size pattern: split by " x " and change the last value
    let size = String(item.size).split(' x ');
    size[size.length - 1] = gypsumSize;
    item.size = size.join(' x ');

    // modelQuantity => total area (so the BOM shows how many sq ft we have)
    item.modelQuantity = String(totalGypsumArea);
    item.modelQuantityDescription = 'sq ft';

    // quantityToOrder => how many sheets
    item.quantityToOrder = String(gypsumSheets);
    item.quantityToOrderDescription = 'sheets';

    return item;
  }

  /**
   * Save changes to a subcategory
   */
  public saveChanges(subcategory: BgBomSubCategory, modelId: string) {
    return this.bgModelService.UpdateBomModel(subcategory, modelId);
  }
}
